import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Box } from '@mui/material';
import { FaceRig } from './faceRig';
import { prefersReducedMotion } from '../fx/motion';

/* «Черри» — a glossy cherry with a living face (logic: faceRig.js).
   Props: emotion, mode ('idle' | 'listening' | 'thinking' | 'speaking'),
   voice() → { level, centroid } of what it says, mic() → loudness of the
   person, size (px, or any CSS width), follow (eyes follow the pointer).
   ref: { react('nod' | 'pop' | 'shake' | 'jump') }.
   Rendering is done straight on the SVG nodes every animation frame — React
   renders the structure once. */

const R = 100;
const INK = '#4d0817';
const MOUTH = '#3f0612';

// closed Catmull-Rom spline through points → smooth SVG path
function smoothPath(pts) {
  const n = pts.length;
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return `${d}Z`;
}

function bodyPath(t, calm) {
  const pts = [];
  const N = 20;
  const w = calm ? 0.4 : 1;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 - Math.PI / 2; // start at the top
    let r =
      R *
      (1 +
        w *
          (0.011 * Math.sin(3 * a + t * 1.3) +
            0.007 * Math.sin(5 * a - t * 0.9)));
    const fromTop = Math.atan2(
      Math.sin(a + Math.PI / 2),
      Math.cos(a + Math.PI / 2)
    );
    r *= 1 - 0.09 * Math.exp(-(fromTop * fromTop) / 0.06); // the dimple of the stem
    r *= 1 + 0.035 * Math.sin(a); // a cherry is fuller at the bottom
    pts.push([Math.cos(a) * r * 1.04, Math.sin(a) * r]);
  }
  return smoothPath(pts);
}

// Polygon clipped by the half-plane a·x + b·y + c >= 0 (Sutherland–Hodgman)
function clip(poly, a, b, c) {
  const out = [];
  for (let i = 0; i < poly.length; i++) {
    const P = poly[i];
    const Q = poly[(i + 1) % poly.length];
    const fp = a * P[0] + b * P[1] + c;
    const fq = a * Q[0] + b * Q[1] + c;
    if (fp >= 0) out.push(P);
    if (fp >= 0 !== fq >= 0) {
      const k = fp / (fp - fq);
      out.push([P[0] + (Q[0] - P[0]) * k, P[1] + (Q[1] - P[1]) * k]);
    }
  }
  return out;
}

const polyPath = (poly) =>
  poly.length < 3
    ? 'M0 0'
    : `M${poly.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('L')}Z`;

/* One eye: an ellipse cut by the upper lid (closing, tilted for sad/angry)
   and the lower lid (smiling squint). side: -1 left, +1 right. */
function eyeShape(rx, ry, close, lidAngle, squint, side) {
  const poly = [];
  for (let i = 0; i < 28; i++) {
    const a = (i / 28) * Math.PI * 2;
    poly.push([Math.cos(a) * rx, Math.sin(a) * ry]);
  }
  const slope = Math.tan((lidAngle * Math.PI) / 180) * -side; // outer corner lower when sad
  const y0 = -ry * 1.08 + 2.16 * ry * close + Math.abs(slope) * rx * 0.45;
  // keep y >= y0 + slope*x  →  -slope*x + y - y0 >= 0
  let out = clip(poly, -slope, 1, -y0);
  const yLow = ry * 1.02 - 2 * ry * squint * 0.55;
  out = clip(out, 0, -1, yLow); // keep y <= yLow
  return { d: polyPath(out), lidY: y0 };
}

const set = (el, attrs) => {
  if (!el) return;
  Object.keys(attrs).forEach((k) => el.setAttribute(k, attrs[k]));
};

const CherryFace = forwardRef(function CherryFace(
  {
    emotion = 'neutral',
    mode = 'idle',
    voice,
    mic,
    size = 320,
    follow = true,
    dark = false,
  },
  ref
) {
  const rig = useMemo(() => new FaceRig({ calm: prefersReducedMotion() }), []);
  const el = useRef({});
  const svgRef = useRef(null);
  const inputs = useRef({ voice, mic });
  inputs.current = { voice, mic };
  const uid = useMemo(() => `cf${Math.random().toString(36).slice(2, 8)}`, []);
  const r = (name) => (node) => {
    el.current[name] = node;
  };

  useImperativeHandle(ref, () => ({ react: (k) => rig.react(k) }), [rig]);
  useEffect(() => rig.setEmotion(emotion), [rig, emotion]);
  useEffect(() => rig.setMode(mode), [rig, mode]);

  useEffect(() => {
    if (!follow) return undefined;
    const move = (e) => {
      const svg = svgRef.current;
      if (!svg) return;
      const b = svg.getBoundingClientRect();
      const cx = b.left + b.width / 2;
      const cy = b.top + b.height * 0.52;
      const span = Math.max(b.width, 240) * 1.6;
      rig.setPointer((e.clientX - cx) / span, (e.clientY - cy) / span);
    };
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerdown', move, { passive: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerdown', move);
    };
  }, [rig, follow]);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const calm = prefersReducedMotion();
    const frame = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      const { voice: v, mic: m } = inputs.current;
      const p = rig.update(dt, {
        voice: v ? v() : null,
        mic: m ? m() : 0,
      });
      const E = el.current;
      // head
      set(E.head, {
        transform: `translate(${p.hx.toFixed(2)} ${(p.hy + R * (1 - p.sy)).toFixed(2)}) rotate(${p.tilt.toFixed(2)}) scale(${p.sx.toFixed(4)} ${p.sy.toFixed(4)})`,
      });
      set(E.body, { d: bodyPath(p.t, calm) });
      set(E.shadow, {
        rx: (74 - p.lift * 1.6).toFixed(1),
        opacity: (0.2 - p.lift * 0.008).toFixed(3),
        cx: (p.hx * 0.6).toFixed(1),
      });
      // stem and leaf sway from their root
      set(E.stem, { transform: `rotate(${p.stem.toFixed(2)} 0 -92)` });
      set(E.leaf, {
        transform: `rotate(${(p.stem * 0.8 + Math.sin(p.t * 1.7) * 4).toFixed(2)} 16 -146)`,
      });
      set(E.gloss, {
        transform: `translate(${(-p.lookX * 3).toFixed(1)} ${(-p.lookY * 2).toFixed(1)})`,
      });

      // eyes
      const lx = p.lookX * 9;
      const ly = p.lookY * 7;
      [-1, 1].forEach((side) => {
        const happy = Math.min(1, Math.max(p.happyEye, side < 0 ? p.wink : 0));
        const sc = Math.max(0.6, p.eyeScale);
        const rx = 13.5 * sc;
        const ry = 17.5 * sc * (1 - 0.55 * happy);
        const close = Math.max(p.blink, p.lidDrop * 0.78, 1 - p.eyeOpen);
        const shape = eyeShape(
          rx,
          ry,
          Math.min(1, close),
          p.lidAngle,
          p.squint,
          side
        );
        const cx = side * 36 + lx;
        const cy = -6 + ly;
        const k = side < 0 ? 'L' : 'R';
        // round eye → «^» eye: a short cross-fade, never two half-eyes
        const ovalOp = 1 - Math.min(1, happy / 0.55);
        const arcOp = Math.min(1, Math.max(0, (happy - 0.35) / 0.5));
        set(E[`eye${k}`], {
          d: shape.d,
          transform: `translate(${cx.toFixed(1)} ${cy.toFixed(1)})`,
          opacity: ovalOp.toFixed(3),
        });
        const shineOp = Math.max(0, 1 - close * 1.6) * ovalOp;
        const s = Math.max(0.5, p.shine);
        set(E[`shine${k}1`], {
          cx: (cx - 4.5 + p.lookX * 2).toFixed(1),
          cy: (cy - 7 + p.lookY * 1.5).toFixed(1),
          r: (4.6 * sc * s).toFixed(2),
          opacity: shineOp.toFixed(3),
        });
        set(E[`shine${k}2`], {
          cx: (cx + 5).toFixed(1),
          cy: (cy + 5.5).toFixed(1),
          r: (2.1 * sc * s).toFixed(2),
          opacity: (shineOp * 0.9).toFixed(3),
        });
        set(E[`arc${k}`], {
          d: `M${(cx - 13).toFixed(1)} ${(cy + 4).toFixed(1)}Q${cx.toFixed(1)} ${(cy - 13).toFixed(1)} ${(cx + 13).toFixed(1)} ${(cy + 4).toFixed(1)}`,
          opacity: arcOp.toFixed(3),
        });
        const shut =
          close > 0.86 ? Math.min(1, (close - 0.86) / 0.1) * ovalOp : 0;
        set(E[`shut${k}`], {
          d: `M${(cx - rx).toFixed(1)} ${(cy + 2).toFixed(1)}Q${cx.toFixed(1)} ${(cy + 7).toFixed(1)} ${(cx + rx).toFixed(1)} ${(cy + 2).toFixed(1)}`,
          opacity: shut.toFixed(3),
        });
        // brows
        const by =
          -40 -
          p.browY * 9 +
          ly * 0.25 -
          (side < 0 ? p.browAsym * 7 : -p.browAsym * 2);
        const inner = p.browTilt * 7;
        const ix = cx - side * 11;
        const ox = cx + side * 12;
        set(E[`brow${k}`], {
          d: `M${ox.toFixed(1)} ${(by + inner * 0.35).toFixed(1)}Q${cx.toFixed(1)} ${(by - 5 - inner * 0.3).toFixed(1)} ${ix.toFixed(1)} ${(by - inner).toFixed(1)}`,
        });
        set(E[`cheek${k}`], {
          cx: (side * 60 + lx * 0.4).toFixed(1),
          cy: (22 + ly * 0.3 - p.squint * 3).toFixed(1),
          opacity: (p.blush * 0.85).toFixed(3),
        });
      });

      // mouth
      const o = Math.max(0, p.mouthOpen);
      const rr = Math.min(1, Math.max(0, p.mouthRound));
      const sm = p.smile;
      const half = Math.max(
        5,
        (21 + Math.max(0, sm) * 6) * p.mouthWide * (1 - 0.5 * rr)
      );
      const mx = lx * 0.35 + p.mouthSkew * 3;
      const my = 40 + ly * 0.25;
      const lift = sm * 8;
      const Lx = mx - half;
      const Ly = my - lift - p.mouthSkew * 4;
      const Rx = mx + half;
      const Ry = my - lift + p.mouthSkew * 4;
      const mid = my + sm * 5;
      const uy = mid - o * (5 + 10 * rr) - rr * o * 4;
      const lyy = mid + o * (28 + Math.max(0, sm) * 6) + o * rr * 4;
      const kx = 0.35 + 0.5 * rr;
      const kl = Math.min(0.95, kx + 0.15);
      const md = `M${Lx.toFixed(1)} ${Ly.toFixed(1)}C${(mx + (Lx - mx) * kx).toFixed(1)} ${uy.toFixed(1)} ${(mx + (Rx - mx) * kx).toFixed(1)} ${uy.toFixed(1)} ${Rx.toFixed(1)} ${Ry.toFixed(1)}C${(mx + (Rx - mx) * kl).toFixed(1)} ${lyy.toFixed(1)} ${(mx + (Lx - mx) * kl).toFixed(1)} ${lyy.toFixed(1)} ${Lx.toFixed(1)} ${Ly.toFixed(1)}Z`;
      set(E.mouth, { d: md });
      set(E.mouthClip, { d: md });
      set(E.teeth, {
        cx: mx.toFixed(1),
        cy: (uy + (mid - uy) * 0.35 - 3).toFixed(1),
        rx: (half * 0.7).toFixed(1),
        opacity: (
          Math.min(1, Math.max(0, (o - 0.18) * 4)) *
          Math.min(1, Math.max(0, sm * 1.6))
        ).toFixed(3),
      });
      set(E.tongue, {
        cx: mx.toFixed(1),
        cy: (lyy - 2).toFixed(1),
        rx: (half * 0.55).toFixed(1),
        opacity: Math.min(1, o * 3).toFixed(3),
      });
      const tg = Math.max(0, p.tongue) * Math.max(0, 1 - o * 3);
      set(E.tongueOut, {
        transform: `translate(${(mx + 7).toFixed(1)} ${(Ry + 3).toFixed(1)}) scale(1 ${Math.max(0.01, tg).toFixed(3)})`,
        opacity: Math.min(1, tg * 1.5).toFixed(3),
      });

      // around the head
      const ox = p.hx;
      const oy = p.hy;
      [0, 1, 2].forEach((i) => {
        const pos = [
          [-112, -72],
          [116, -36],
          [102, 74],
        ][i];
        const sc = p.sparkle * (0.6 + 0.4 * Math.sin(p.t * 4.2 + i * 2.1));
        set(E[`spark${i}`], {
          transform: `translate(${(pos[0] + ox).toFixed(1)} ${(pos[1] + oy).toFixed(1)}) rotate(${(p.t * 50 + i * 40).toFixed(1)}) scale(${Math.max(0.001, sc).toFixed(3)})`,
          opacity: Math.min(1, p.sparkle * 1.3).toFixed(3),
        });
      });
      for (let i = 0; i < 6; i++) {
        const h = p.heartList[i];
        if (!h) {
          set(E[`heart${i}`], { opacity: 0 });
          continue;
        }
        const life = h.age / 2.3;
        set(E[`heart${i}`], {
          transform: `translate(${(h.x + ox + Math.sin(h.age * 3 + h.seed) * 10).toFixed(1)} ${(-70 + oy - h.age * 48).toFixed(1)}) scale(${(0.7 + 0.3 * Math.sin(h.age * 6)).toFixed(3)})`,
          opacity: (Math.min(1, h.age * 5) * (1 - life) * p.hearts).toFixed(3),
        });
      }
      const tp = (p.t * 0.55) % 1;
      set(E.tear, {
        transform: `translate(${(-44 + lx - tp * 5).toFixed(1)} ${(12 + ly + tp * 34).toFixed(1)}) scale(${(0.7 + tp * 0.4).toFixed(3)})`,
        opacity: (p.tear * Math.min(1, tp * 6) * (1 - tp * tp * tp)).toFixed(3),
      });
      const sp = (p.t * 0.4) % 1;
      set(E.sweat, {
        transform: `translate(${(80 + ox).toFixed(1)} ${(-66 + oy + sp * 16).toFixed(1)})`,
        opacity: (p.sweat * (1 - sp) * Math.min(1, sp * 5)).toFixed(3),
      });
      [0, 1, 2].forEach((i) => {
        const zp = (p.t * 0.33 + i / 3) % 1;
        set(E[`z${i}`], {
          transform: `translate(${(66 + ox + zp * 42).toFixed(1)} ${(-86 + oy - zp * 62).toFixed(1)}) scale(${(0.6 + zp * 0.9).toFixed(3)})`,
          opacity: (p.zzz * Math.sin(zp * Math.PI)).toFixed(3),
        });
        const np = (p.t * 0.38 + i / 3) % 1;
        set(E[`note${i}`], {
          transform: `translate(${(-82 + ox - np * 30).toFixed(1)} ${(-64 + oy - np * 66).toFixed(1)}) rotate(${(Math.sin(np * 6) * 14).toFixed(1)})`,
          opacity: (p.notes * Math.sin(np * Math.PI)).toFixed(3),
        });
        const d = 0.75 + 0.35 * Math.max(0, Math.sin(p.t * 4 - i * 0.9));
        const dpos = [
          [70, -96],
          [90, -118],
          [115, -136],
        ][i];
        set(E[`dot${i}`], {
          transform: `translate(${(dpos[0] + ox).toFixed(1)} ${(dpos[1] + oy).toFixed(1)}) scale(${(d * Math.max(0.001, p.dots)).toFixed(3)})`,
          opacity: Math.min(1, p.dots).toFixed(3),
        });
      });
      set(E.question, {
        transform: `translate(${(92 + ox).toFixed(1)} ${(-98 + oy + Math.sin(p.t * 2.4) * 4).toFixed(1)}) rotate(${(Math.sin(p.t * 1.7) * 12).toFixed(1)}) scale(${Math.max(0.001, p.question).toFixed(3)})`,
        opacity: Math.min(1, p.question).toFixed(3),
      });
      const wl = 1 + Math.min(1, p.mic * 1.8) * 0.3;
      [-1, 1].forEach((side) => {
        set(E[side < 0 ? 'waveL' : 'waveR'], {
          transform: `translate(${(side * 124 + ox).toFixed(1)} ${(oy + 4).toFixed(1)}) scale(${(side * wl).toFixed(3)} ${wl.toFixed(3)})`,
          opacity: Math.min(1, p.waves).toFixed(3),
        });
      });
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [rig]);

  const soft = dark ? '#f1e3e6' : '#6b4b52';
  const heart =
    'M0 4C-6 -2 -12 -6 -12 -11C-12 -16 -7 -18 -4 -16C-2 -15 -1 -13 0 -11C1 -13 2 -15 4 -16C7 -18 12 -16 12 -11C12 -6 6 -2 0 4Z';
  const star = 'M0 -12L3 -3L12 0L3 3L0 12L-3 3L-12 0L-3 -3Z';

  return (
    <Box
      sx={{
        width: size,
        maxWidth: '100%',
        aspectRatio: '1 / 1.06',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <svg
        ref={svgRef}
        viewBox="-165 -190 330 350"
        width="100%"
        height="100%"
        role="img"
        aria-label="Черри"
        style={{ overflow: 'visible', display: 'block' }}
      >
        <defs>
          <radialGradient id={`${uid}body`} cx="34%" cy="28%" r="80%">
            <stop offset="0%" stopColor="#ff7d90" />
            <stop offset="45%" stopColor="#e3304f" />
            <stop offset="100%" stopColor="#97102c" />
          </radialGradient>
          <linearGradient id={`${uid}leaf`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9be27a" />
            <stop offset="100%" stopColor="#3c9a3f" />
          </linearGradient>
          <radialGradient id={`${uid}cheek`}>
            <stop offset="0%" stopColor="#ffb3c1" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#ffb3c1" stopOpacity="0" />
          </radialGradient>
          <clipPath id={`${uid}mouth`}>
            <path ref={r('mouthClip')} />
          </clipPath>
        </defs>

        <ellipse
          ref={r('shadow')}
          cx="0"
          cy={R + 16}
          rx="74"
          ry="10"
          fill="#3a0a14"
          opacity="0.2"
        />

        {/* listening waves */}
        <g ref={r('waveL')} opacity="0">
          <path
            d="M-6 -16Q-16 0 -6 16"
            fill="none"
            stroke={soft}
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M-16 -26Q-32 0 -16 26"
            fill="none"
            stroke={soft}
            strokeWidth="4"
            strokeLinecap="round"
            opacity="0.6"
          />
        </g>
        <g ref={r('waveR')} opacity="0">
          <path
            d="M-6 -16Q-16 0 -6 16"
            fill="none"
            stroke={soft}
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M-16 -26Q-32 0 -16 26"
            fill="none"
            stroke={soft}
            strokeWidth="4"
            strokeLinecap="round"
            opacity="0.6"
          />
        </g>

        <g ref={r('head')}>
          <g ref={r('stem')}>
            <path
              d="M0 -92C2 -112 8 -130 18 -150"
              fill="none"
              stroke="#6d4122"
              strokeWidth="7"
              strokeLinecap="round"
            />
            <g ref={r('leaf')}>
              <path
                d="M16 -146C26 -170 56 -172 70 -160C56 -144 32 -138 16 -146Z"
                fill={`url(#${uid}leaf)`}
              />
              <path
                d="M18 -147C34 -154 50 -158 66 -160"
                fill="none"
                stroke="#2f7d33"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.7"
              />
            </g>
          </g>
          <path ref={r('body')} fill={`url(#${uid}body)`} />
          <g ref={r('gloss')}>
            <ellipse
              cx="-44"
              cy="-50"
              rx="24"
              ry="13"
              transform="rotate(-32 -44 -50)"
              fill="#fff"
              opacity="0.42"
            />
            <circle cx="-20" cy="-66" r="5" fill="#fff" opacity="0.5" />
          </g>
          <ellipse
            ref={r('cheekL')}
            cx="-60"
            cy="22"
            rx="17"
            ry="10"
            fill={`url(#${uid}cheek)`}
          />
          <ellipse
            ref={r('cheekR')}
            cx="60"
            cy="22"
            rx="17"
            ry="10"
            fill={`url(#${uid}cheek)`}
          />
          {['L', 'R'].map((k) => (
            <g key={k}>
              <path ref={r(`eye${k}`)} fill={INK} />
              <circle ref={r(`shine${k}1`)} fill="#fff" />
              <circle ref={r(`shine${k}2`)} fill="#fff" />
              <path
                ref={r(`arc${k}`)}
                fill="none"
                stroke={INK}
                strokeWidth="5.5"
                strokeLinecap="round"
                opacity="0"
              />
              <path
                ref={r(`shut${k}`)}
                fill="none"
                stroke={INK}
                strokeWidth="5"
                strokeLinecap="round"
                opacity="0"
              />
              <path
                ref={r(`brow${k}`)}
                fill="none"
                stroke="#5a0a1b"
                strokeWidth="5.5"
                strokeLinecap="round"
                opacity="0.92"
              />
            </g>
          ))}
          <path
            ref={r('mouth')}
            fill={MOUTH}
            stroke={MOUTH}
            strokeWidth="4.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <g clipPath={`url(#${uid}mouth)`}>
            <ellipse
              ref={r('teeth')}
              cx="0"
              cy="30"
              rx="14"
              ry="6"
              fill="#fff"
              opacity="0"
            />
            <ellipse
              ref={r('tongue')}
              cx="0"
              cy="50"
              rx="12"
              ry="9"
              fill="#ff7a8f"
              opacity="0"
            />
          </g>
          <g ref={r('tongueOut')} opacity="0">
            <path
              d="M-7 0C-7 9 7 9 7 0Z"
              fill="#ff8da0"
              stroke={MOUTH}
              strokeWidth="2"
            />
          </g>
          <path
            ref={r('tear')}
            d="M0 -7C4 -1 6 3 6 6C6 10 3 12 0 12C-3 12 -6 10 -6 6C-6 3 -4 -1 0 -7Z"
            fill="#8fd0ff"
            opacity="0"
          />
        </g>

        {[0, 1, 2].map((i) => (
          <path
            key={`s${i}`}
            ref={r(`spark${i}`)}
            d={star}
            fill="#ffd166"
            opacity="0"
          />
        ))}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <path
            key={`h${i}`}
            ref={r(`heart${i}`)}
            d={heart}
            fill="#ff4f6d"
            opacity="0"
          />
        ))}
        <path
          ref={r('sweat')}
          d="M0 -9C5 -2 7 3 7 6C7 10 4 13 0 13C-4 13 -7 10 -7 6C-7 3 -5 -2 0 -9Z"
          fill="#9fdcff"
          opacity="0"
        />
        {[0, 1, 2].map((i) => (
          <text
            key={`z${i}`}
            ref={r(`z${i}`)}
            fontSize="18"
            fontWeight="800"
            fill={soft}
            opacity="0"
            fontFamily="system-ui, sans-serif"
          >
            z
          </text>
        ))}
        {[0, 1, 2].map((i) => (
          <text
            key={`n${i}`}
            ref={r(`note${i}`)}
            fontSize="22"
            fill={soft}
            opacity="0"
            fontFamily="system-ui, sans-serif"
          >
            {i === 1 ? '♫' : '♪'}
          </text>
        ))}
        {[0, 1, 2].map((i) => (
          <circle
            key={`d${i}`}
            ref={r(`dot${i}`)}
            r={[5, 7, 10][i]}
            fill={soft}
            opacity="0"
          />
        ))}
        <text
          ref={r('question')}
          fontSize="40"
          fontWeight="900"
          fill={soft}
          opacity="0"
          textAnchor="middle"
          fontFamily="system-ui, sans-serif"
        >
          ?
        </text>
      </svg>
    </Box>
  );
});

export default CherryFace;
